import {
    anonymizedContactInfoFactory,
    contactInfoZodObject,
} from "@@/shared/locations/contact-info";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { repeat } from "@@/utilities/repeat";
import { z } from "zod";
import { signatureFactory } from "../signature-factory";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "./_order-base.event";

const orderContactInfoChangedEventZodObject = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("CONTACT_INFO_CHANGED"),
        changedFrom: contactInfoZodObject,
        changedTo: contactInfoZodObject,
        changedBy: z.object({
            userId: userIdZodSchema,
            firstName: z.string(),
            lastName: z.string(),
        }),
        reason: z.string().optional(),
    }),
);
type OrderContactInfoChangedEvent = z.infer<
    typeof orderContactInfoChangedEventZodObject
>;

// Factory params
const _orderContactInfoChangedEventFactoryParamsZodObject =
    orderContactInfoChangedEventZodObject.omit({
        _id: true,
        _type: true,
        _createdAt: true,
        type: true,
        signature: true,
    });
type FactoryParams = z.infer<
    typeof _orderContactInfoChangedEventFactoryParamsZodObject
>;

const orderContactInfoChangedEventFactory = (
    params: FactoryParams,
): OrderContactInfoChangedEvent => ({
    ..._orderEventBaseFactory({
        orderId: params.orderId,
        orderGroupId: params.orderGroupId,
        _version: params._version,
        providerId: params.providerId,
        providerSlug: params.providerSlug,
        signature: signatureFactory(params.orderId, "CONTACT_INFO_CHANGED", [
            `source__${params.changedFrom._id}`,
            `target__${params.changedTo._id}`,
        ]),
    }),

    type: "CONTACT_INFO_CHANGED",
    changedFrom: params.changedFrom,
    changedTo: params.changedTo,
    changedBy: params.changedBy,
    reason: params.reason,
});

const isOrderContactInfoChangedEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderContactInfoChangedEvent => {
    return (
        !!event &&
        event._type === "ORDER_EVENT" &&
        event?.type === "CONTACT_INFO_CHANGED"
    );
};

const anonymizeOrderContactInfoChangedEvent = (
    event: OrderContactInfoChangedEvent,
): OrderContactInfoChangedEvent => ({
    ...event,
    changedFrom: anonymizedContactInfoFactory(event.changedFrom),
    changedTo: anonymizedContactInfoFactory(event.changedTo),
    changedBy: {
        ...event.changedBy,
        firstName: repeat("#", event.changedBy.firstName.length).join(""),
        lastName: repeat("#", event.changedBy.lastName.length).join(""),
    },
});

export {
    anonymizeOrderContactInfoChangedEvent,
    isOrderContactInfoChangedEvent,
    orderContactInfoChangedEventFactory,
    orderContactInfoChangedEventZodObject,
};
export type { OrderContactInfoChangedEvent };
